import { useState } from 'react';
import { useTranslate, HttpError } from '@refinedev/core';
import { Edit, useForm } from '@refinedev/antd';
import { Form, Input, Select } from 'antd';
import type {
  BannerActionDetailOut,
  BannerActionIn,
} from '@/api/generated/schemas';
import {
  BannerSize,
  BannerActionKind,
  ImageKind,
} from '@/api/generated/schemas';
import { ImageUploadField } from '@/components/common/ImageUploadField';
import { RATIO, ratioLabel } from '@/components/user_catalog/creative/creative';
import { BannerActionTargetField } from '@/components/user_catalog/banner_actions/BannerActionTargetField';
import {
  targetAwareErrorNotification,
  targetRefOf,
} from '@/components/user_catalog/banner_actions/bannerActionTarget';

export const BannerActionEdit = () => {
  const t = useTranslate();
  const { formProps, saveButtonProps, form, query } = useForm<
    BannerActionDetailOut,
    HttpError,
    BannerActionIn
  >({
    resource: 'user_catalog.banner_actions',
    mutationMeta: { method: 'patch' },
    redirect: 'show',
    errorNotification: targetAwareErrorNotification,
  });
  const record = query?.data?.data;

  /** Crop to the shape the app will render, so upload loses nothing. */
  const size = Form.useWatch('size', form) ?? record?.size;
  const aspect = size ? RATIO[size].w / RATIO[size].h : undefined;

  const kind = Form.useWatch('kind', form) ?? record?.kind;

  // The API returns the resolved target object, but the form edits the
  // wire-format target_ref — derive the initial value.
  const currentTargetRef = targetRefOf(record?.target);

  /**
   * The image was cropped to the old size's ratio, so it cannot survive a
   * size change: drop it and make the editor upload one that fits.
   */
  const [imageCleared, setImageCleared] = useState(false);
  const clearImage = () => {
    setImageCleared(true);
    form?.setFieldsValue({ image_storage_key: null });
  };

  return (
    <Edit saveButtonProps={saveButtonProps}>
      <Form
        {...formProps}
        initialValues={{
          ...formProps.initialValues,
          target_ref: currentTargetRef,
        }}
        layout="vertical"
      >
        <Form.Item
          name="name"
          label={t('user_catalog.banner_actions.fields.name')}
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="size"
          label={t('user_catalog.banner_actions.fields.size')}
          extra={t('user_catalog.banner_actions.hints.size_clears_image')}
        >
          <Select
            onChange={clearImage}
            options={[
              {
                label: t('user_catalog.banner_actions.size.large'),
                value: BannerSize.large,
              },
              {
                label: t('user_catalog.banner_actions.size.small'),
                value: BannerSize.small,
              },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="kind"
          label={t('user_catalog.banner_actions.fields.kind')}
          extra={
            kind === BannerActionKind.user_profile
              ? t('user_catalog.banner_actions.hints.user_profile_no_route')
              : undefined
          }
        >
          <Select
            onChange={() => form?.setFieldsValue({ target_ref: null })}
            options={Object.values(BannerActionKind).map((value) => ({
              label: t(`user_catalog.banner_actions.kind.${value}`),
              value,
            }))}
          />
        </Form.Item>

        <BannerActionTargetField
          kind={kind}
          currentTargetRef={currentTargetRef}
        />

        <Form.Item name="image_storage_key" hidden>
          <Input />
        </Form.Item>
        <ImageUploadField
          key={size}
          imageKind={ImageKind.banners}
          currentUrl={imageCleared ? null : record?.image_url}
          onUpload={(key) => form?.setFieldsValue({ image_storage_key: key })}
          label={
            size
              ? `${t('user_catalog.banner_actions.fields.image_url')} (${ratioLabel(size)})`
              : t('user_catalog.banner_actions.fields.image_url')
          }
          aspect={aspect}
        />
      </Form>
    </Edit>
  );
};
